import React, { useState, useEffect } from 'react';
import { getReportedComments, postCommentAction } from '../../api';
import Paging from '../../common/layouts/page/Paging';

interface ReportedComment {
    id: number;
    writerNickname: string;
    content: string;
    targetType: string;
    reportReason: string;
    reportedAt: string;
    status: 'PENDING' | 'BLOCKED' | 'DISMISSED';
}

const ReportedCommentTable: React.FC = () => {
    const [reports, setReports] = useState<ReportedComment[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const loadData = async (page: number) => {
        setLoading(true);
        try {
            const data = await getReportedComments(page, 10);
            if (data) {
                setReports(data.content || []);
                setTotalPages(data.totalPages || 1);
                setTotalCount(data.totalElements || 0);
                setCurrentPage(page);
            }
        } catch (error) {
            console.error("Failed to load reported comments", error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '';
        try {
            const date = new Date(dateStr);
            const yyyy = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const dd = String(date.getDate()).padStart(2, '0');
            const hh = String(date.getHours()).padStart(2, '0');
            const min = String(date.getMinutes()).padStart(2, '0');
            const ss = String(date.getSeconds()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
        } catch {
            return dateStr;
        }
    };

    useEffect(() => {
        loadData(1);
    }, []);

    const handleAction = async (id: number, action: 'BLOCK' | 'DISMISS') => {
        if (!window.confirm(`${action === 'BLOCK' ? '차단' : '기각'} 처리를 진행하시겠습니까?`)) return;
        try {
            await postCommentAction(id, action);
            alert("처리가 완료되었습니다.");
            loadData(currentPage);
        } catch (error) {
            alert("처리에 실패했습니다.");
        }
    };

    return (
        <div className="col-12">
            <div className="card border border-[#f1f5f9] shadow-sm rounded-[16px] overflow-hidden">
                <div className="card-header bg-white border-0 pt-4 pb-3 d-flex justify-content-between align-items-center px-4">
                    <h5 className="card-title fw-bold text-dark mb-0">신고 댓글 내역</h5>
                    <div className="badge bg-soft-primary text-primary px-3 py-2 rounded-pill fw-bold" style={{ backgroundColor: '#eff6ff', color: '#3b82f6' }}>
                        총 {totalCount}건
                    </div>
                </div>
                <div className="card-body p-0">
                    <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', minHeight: '180px' }}>
                        {loading ? (
                            <div className="p-5 text-center text-muted">불러오는 중...</div>
                        ) : reports.length === 0 ? (
                            <div className="p-5 text-center text-muted">신고된 댓글이 없습니다.</div>
                        ) : (
                            <table className="table table-hover align-middle mb-0" style={{ minWidth: '1000px', tableLayout: 'fixed' }}>
                                <thead style={{ backgroundColor: '#f8fafc' }}>
                                    <tr>
                                        <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '150px' }}>작성자</th>
                                        <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '250px' }}>내용</th>
                                        <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '100px' }}>대상</th>
                                        <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '180px' }}>신고 사유</th>
                                        <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '170px' }}>신고 일시</th>
                                        <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '100px' }}>상태</th>
                                        <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '150px' }}>작업</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reports.map((report) => (
                                        <tr key={report.id}>
                                            <td className="px-4 py-3 fw-bold text-[#475569]">{report.writerNickname}</td>
                                            <td className="px-4 py-3">
                                                <div className="text-truncate text-dark" style={{ maxWidth: '250px' }}>{report.content}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="badge rounded-pill px-3 py-2 fw-bold" style={{ backgroundColor: '#f1f5f9', color: '#64748b' }}>{report.targetType}</span>
                                            </td>
                                            <td className="px-4 py-3 text-danger small fw-medium">{report.reportReason}</td>
                                            <td className="px-4 py-3 text-muted small">{formatDate(report.reportedAt)}</td>
                                            <td className="px-4 py-3">
                                                {report.status === 'PENDING' && <span className="badge rounded-pill px-3 py-2 fw-bold" style={{ backgroundColor: '#fffbeb', color: '#f59e0b' }}>대기</span>}
                                                {report.status === 'BLOCKED' && <span className="badge rounded-pill px-3 py-2 fw-bold" style={{ backgroundColor: '#fef2f2', color: '#ef4444' }}>차단됨</span>}
                                                {report.status === 'DISMISSED' && <span className="badge rounded-pill px-3 py-2 fw-bold" style={{ backgroundColor: '#f1f5f9', color: '#64748b' }}>기각</span>}
                                            </td>
                                            <td className="px-4 py-3" style={{ whiteSpace: 'nowrap' }}>
                                                {report.status === 'PENDING' && (
                                                    <div className="d-flex gap-2">
                                                        <button 
                                                            onClick={() => handleAction(report.id, 'BLOCK')}
                                                            className="btn btn-xs btn-outline-danger rounded px-2.5 py-1 fs-11 fw-bold"
                                                        >
                                                            차단
                                                        </button>
                                                        <button 
                                                            onClick={() => handleAction(report.id, 'DISMISS')}
                                                            className="btn btn-xs btn-outline-secondary rounded px-2.5 py-1 fs-11 fw-bold"
                                                        >
                                                            기각
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
                <div className="card-footer bg-white border-top-0 d-flex justify-content-center py-4 px-4">
                    <Paging
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={loadData}
                    />
                </div>
            </div>
        </div>
    );
};

export default ReportedCommentTable;
